/**
 * Split.io Client Factory
 * -----------------------
 * Initializes and manages a singleton Split.io client instance.
 *
 * @author  Harvinder Singh <harvinder@maropost.com>
 * @created 2025-09-12
 */

import { SplitFactory } from "@splitsoftware/splitio";
import { AppConfig } from "@/lib/config";
import { useUserStore } from "@/lib/stores";
import { constants } from "@/lib/utils/constants";

let client = null;

/**
 * Initializes the Split.io client if not already created.
 */
export async function createSplitFactory() {
  if (client) return; // ✅ Already initialized

  const factory = SplitFactory({
    core: {
      authorizationKey: AppConfig.SPLIT_AUTHORIZATION_KEY || "",
    },
  });

  const userStore = useUserStore();
  const accountId = userStore.getAccountId?.toString() || "";

  client = factory.client(accountId);

  return new Promise((resolve, reject) => {
    client?.on(client.Event.SDK_READY, () => resolve());
    client?.on(client.Event.SDK_READY_TIMED_OUT, () =>
      reject(new Error("Split.io SDK initialization timed out"))
    );
    client?.on(client.Event.SDK_INIT_TIMED_OUT, () =>
      reject(new Error("Split.io SDK init failed"))
    );
  });
}

/**
 * Gets a feature flag treatment.
 * @param key - The Split.io feature flag key
 * @returns true if treatment is "on", false otherwise
 */
export async function getSplitKey(key) {
  if (!client) {
    throw new Error("Split client not initialized. Call createSplitFactory() first.");
  }

  const treatment = await client.getTreatment(key);
  //temp code for staging and uat
  if(key == constants.SPLIT_IO_KEYS.isUIDAccount){
    return treatment !== "on"
  }
  //temp code ends here
  return treatment === "on";
}
